/**
 * NextPlayGuideHelper — Smart music autoplay with playback history
 *
 * Ported from App-WebTV/src/utils/nextPlayGuideHelper.js
 * Features:
 * - Multiple guide types (genre, category, playlist, artist)
 * - localStorage-backed history to avoid repeats
 * - Auto-reset when all items in a group have been played
 */

const HISTORY_KEY_PREFIX = "next_play_guide_history_";

const GUIDE_QUERY_PARAMS = {
    TYPE: "nextPlayGuideType",
    SLUG: "nextPlayGuideSlug",
    NAME: "nextPlayGuideName",
};

export const GuideType = {
    GENRE: "genre",
    CATEGORY: "category",
    PLAYLIST: "playlist",
    ARTIST: "artist",
};

/**
 * Create a NextPlayGuide entity from content data.
 * A guide supplied by the user's entrypoint takes precedence over content metadata.
 * Falls back to category → genre if nextPlayGuide is not specified.
 */
export function getNextPlayGuide(contentData, entrypointGuide) {
    const normalizedEntrypointGuide = _normalizeGuide(entrypointGuide);
    if (normalizedEntrypointGuide) {
        return normalizedEntrypointGuide;
    }

    if (!contentData) {
        return { type: GuideType.CATEGORY, slug: "", name: "" };
    }

    // If API specifies a valid guide, use it
    const serverGuide = _normalizeGuide(contentData.nextPlayGuide);
    if (serverGuide) {
        return serverGuide;
    }

    // Fallback: category → genre
    const categorySlug = contentData.categoryKeys?.[0];
    if (categorySlug) {
        return { type: GuideType.CATEGORY, slug: categorySlug, name: "" };
    }

    const genreSlug = contentData.genreKeys?.[0];
    if (genreSlug) {
        return { type: GuideType.GENRE, slug: genreSlug, name: "" };
    }

    return { type: GuideType.CATEGORY, slug: "", name: "" };
}

function _normalizeGuide(guide) {
    if (!guide?.type || !guide?.slug) return null;

    const type = String(guide.type).toLowerCase();
    if (!Object.values(GuideType).includes(type)) return null;

    return {
        type,
        slug: String(guide.slug),
        name: guide.name ? String(guide.name) : "",
    };
}

/**
 * Resolve the category or genre that determines a showcase row's contents.
 * Explicit row keys are authoritative; actionLink is a legacy/fill fallback.
 */
export function getShowcaseNextPlayGuide(showcaseRow) {
    if (!showcaseRow) return null;

    const categorySlug = showcaseRow.categoryKeys?.find(Boolean);
    if (categorySlug) {
        return {
            type: GuideType.CATEGORY,
            slug: String(categorySlug),
            name: showcaseRow.title ? String(showcaseRow.title) : "",
        };
    }

    const genreSlug = showcaseRow.genreKeys?.find(Boolean);
    if (genreSlug) {
        return {
            type: GuideType.GENRE,
            slug: String(genreSlug),
            name: showcaseRow.title ? String(showcaseRow.title) : "",
        };
    }

    if (typeof showcaseRow.actionLink !== "string") return null;

    const [type, slug] = showcaseRow.actionLink.split(";");
    if (![GuideType.CATEGORY, GuideType.GENRE].includes(type) || !slug) {
        return null;
    }

    return {
        type,
        slug,
        name: showcaseRow.title ? String(showcaseRow.title) : "",
    };
}

/**
 * Read an entrypoint guide from URL search parameters.
 */
export function getNextPlayGuideFromSearchParams(searchParams) {
    if (!searchParams?.get) return null;

    return _normalizeGuide({
        type: searchParams.get(GUIDE_QUERY_PARAMS.TYPE),
        slug: searchParams.get(GUIDE_QUERY_PARAMS.SLUG),
        name: searchParams.get(GUIDE_QUERY_PARAMS.NAME),
    });
}

/**
 * Carry an entrypoint guide through music detail and player navigation.
 */
export function addNextPlayGuideToUrl(url, guide) {
    const normalizedGuide = _normalizeGuide(guide);
    if (!url || !normalizedGuide) return url;

    const [urlWithoutHash, hash] = String(url).split("#", 2);
    const [pathname, queryString] = urlWithoutHash.split("?", 2);
    const searchParams = new URLSearchParams(queryString || "");

    searchParams.set(GUIDE_QUERY_PARAMS.TYPE, normalizedGuide.type);
    searchParams.set(GUIDE_QUERY_PARAMS.SLUG, normalizedGuide.slug);

    if (normalizedGuide.name) {
        searchParams.set(GUIDE_QUERY_PARAMS.NAME, normalizedGuide.name);
    } else {
        searchParams.delete(GUIDE_QUERY_PARAMS.NAME);
    }

    const nextUrl = `${pathname}?${searchParams.toString()}`;
    return hash ? `${nextUrl}#${hash}` : nextUrl;
}

/**
 * Build API query parameters for fetching next music content.
 */
export function buildQueryParameters(guide) {
    const params = {
        kind: "music",
        sortBinName: "createdAt",
        sortType: "desc",
        lazyLoading: true,
        pageIndex: 20,
        pageNumber: 1,
    };

    if (!guide?.slug) return params;

    switch (guide.type) {
        case GuideType.GENRE:
            params.genre = guide.slug;
            break;
        case GuideType.PLAYLIST:
            params.playlist = guide.slug;
            break;
        case GuideType.ARTIST:
            params.artist = guide.name || guide.slug;
            break;
        case GuideType.CATEGORY:
        default:
            params.category = guide.slug;
    }

    return params;
}

// ── Playback History Management ─────────────────────────────────────────

function _getHistoryKey(guide) {
    return HISTORY_KEY_PREFIX + guide.type + "_" + guide.slug;
}

export function getPlaybackHistory(guide) {
    try {
        const key = _getHistoryKey(guide);
        const json = localStorage.getItem(key);
        return json ? JSON.parse(json) : [];
    } catch {
        return [];
    }
}

function savePlaybackHistory(guide, slugs) {
    try {
        const key = _getHistoryKey(guide);
        localStorage.setItem(key, JSON.stringify(slugs));
    } catch { }
}

export function clearPlaybackHistory(guide) {
    try {
        localStorage.removeItem(_getHistoryKey(guide));
    } catch { }
}

/**
 * Mark an item as played in the guide's history.
 */
export function markAsPlayed(slug, guide) {
    if (!slug || !guide) return;
    const played = getPlaybackHistory(guide);
    if (!played.includes(slug)) {
        played.push(slug);
        savePlaybackHistory(guide, played);
    }
}

// ── Filtering & Selection ───────────────────────────────────────────────

/**
 * Filter out current and already-played items.
 * Auto-resets history if no unplayed items remain (looped playlist).
 */
export function filterPlayedItems(musicItems, currentSlug, guide) {
    if (!musicItems?.length) {
        return { items: [], historyWasReset: false };
    }

    const played = getPlaybackHistory(guide);

    let filtered = musicItems.filter(
        (item) => item.slug && item.slug !== currentSlug && !played.includes(item.slug)
    );

    // If nothing left, reset history and re-filter
    if (filtered.length === 0 && musicItems.length > 0) {
        clearPlaybackHistory(guide);
        filtered = musicItems.filter((item) => item.slug && item.slug !== currentSlug);
        return { items: filtered, historyWasReset: true };
    }

    return { items: filtered, historyWasReset: false };
}

/**
 * Select the next music item (first unplayed in sorted order).
 */
export function selectNextMusic(filteredItems) {
    if (!filteredItems?.length) return null;
    return filteredItems[0];
}

// ── Default Export ──────────────────────────────────────────────────────

const NextPlayGuideHelper = {
    GuideType,
    getNextPlayGuide,
    getShowcaseNextPlayGuide,
    getNextPlayGuideFromSearchParams,
    addNextPlayGuideToUrl,
    buildQueryParameters,
    getPlaybackHistory,
    markAsPlayed,
    clearPlaybackHistory,
    filterPlayedItems,
    selectNextMusic,
};

export default NextPlayGuideHelper;
